import { useMemo, useState, Key } from 'react';
import { Tooltip } from 'antd';
import { Button, DropdownMenu, Modal, SIZE, Stack, Text } from '@quinyx/ui';
import { useLocation } from 'react-router-dom';

import { IDeletedCustomer } from '@/api/interfaces/customer';
import Table from '@/components/Table';
import { DATE_FORMAT } from '@/constants/time';
import {
  useDeletedCustomersQueue,
  useUndoDeleteCustomer,
  useUpdateCustomerDeleteBy,
} from '@/hooks/customer';
import { sortDateString, sortNumber, sortString } from '@/utils';
import { format } from '@/utils/time';
import { withCustomerLayout } from '@/layouts/customer';
import { getFilterProps } from '@/components/Table/utils';
import { useTranslate } from '@/hooks/translate';

const DELETION_STATUS_KEYS = [
  'deletionStatus.inQueue',
  'deletionStatus.inProgress',
  'deletionStatus.done',
  'deletionStatus.failed',
  'deletionStatus.partiallyFailed',
  'deletionStatus.heavyDataRetry',
] as const;

const getRowKey = (customer: IDeletedCustomer) => customer.customerId;

const DeletionQueueRowActions = ({ customerId }: { customerId: number }) => {
  const { t } = useTranslate();
  const { undoDeleteCustomer } = useUndoDeleteCustomer();
  const { updateCustomerDeleteBy } = useUpdateCustomerDeleteBy();
  const [isDeleteTodayModalOpen, setDeleteTodayModalOpen] = useState(false);

  const handleDeleteToday = () => {
    setDeleteTodayModalOpen(true);
  };

  const handleConfirmDeleteToday = () => {
    updateCustomerDeleteBy([customerId, format(new Date(), DATE_FORMAT)]);
    setDeleteTodayModalOpen(false);
  };

  const handleRemoveFromQueue = () => {
    undoDeleteCustomer([customerId]);
  };

  return (
    <>
      <DropdownMenu
        trigger={
          <Button
            data-test-id={`deletion-queue-actions-${customerId}`}
            aria-label={t('customer.rowActions')}
            icon="context"
            variant="text"
          />
        }>
        <DropdownMenu.Item
          data-test-id="delete-queue-action-delete-today"
          onClick={handleDeleteToday}>
          {t('customer.deleteToday')}
        </DropdownMenu.Item>
        <DropdownMenu.Item
          data-test-id="delete-queue-action-remove-from-queue"
          onClick={handleRemoveFromQueue}>
          {t('customer.removeFromDeletionQueue')}
        </DropdownMenu.Item>
      </DropdownMenu>

      <Modal
        open={isDeleteTodayModalOpen}
        onOpenChange={setDeleteTodayModalOpen}>
        <Modal.Header>
          <Modal.CloseButton
            ariaLabel="close-delete-today-modal"
            dataTestId="close-delete-today-modal"
          />
        </Modal.Header>
        <Modal.Content>
          <Modal.HeadlineText>{t('customer.deleteToday')}</Modal.HeadlineText>
          <Stack spacing={SIZE.M} mt={SIZE.S}>
            <Text variant="smallRegular" color="SurfaceOnSurfaceAlt">
              {t('customer.scheduleDeletionForTodayConfirm')}
            </Text>
            <Stack direction="row" spacing={SIZE.S} justifyContent="flex-end">
              <Button
                data-test-id="cancel-delete-today-button"
                text={t('common.cancel')}
                variant="text"
                onClick={() => setDeleteTodayModalOpen(false)}
              />
              <Button
                data-test-id="confirm-delete-today-button"
                text={t('customer.scheduleDeletion')}
                intent="danger"
                onClick={handleConfirmDeleteToday}
              />
            </Stack>
          </Stack>
        </Modal.Content>
      </Modal>
    </>
  );
};

const Queue = () => {
  const { t } = useTranslate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);

  const { data = [], isLoading } = useDeletedCustomersQueue();

  const [searchText, setSearchText] = useState<Key>(() => {
    const name = searchParams.get('name');
    return name || '';
  });
  const [searchedColumn, setSearchedColumn] = useState<Key>(() => {
    const name = searchParams.get('name');
    return name ? 'name' : '';
  });

  const columns = useMemo(
    () => [
      {
        title: t('customer.customers'),
        key: 'name',
        sorter: (a: IDeletedCustomer, b: IDeletedCustomer) =>
          sortString(a.name ?? a.customerName, b.name ?? b.customerName),
        ...getFilterProps(
          'name',
          setSearchText,
          setSearchedColumn,
          searchedColumn,
          searchText,
        ),
        render: ({ name }: IDeletedCustomer) => name ?? '',
      },
      {
        title: t('customer.deleteBy'),
        dataIndex: 'deleteBy',
        key: 'deleteBy',
        sorter: (a: IDeletedCustomer, b: IDeletedCustomer) =>
          sortDateString(
            a.deleteBy ?? a.deletedAt ?? '',
            b.deleteBy ?? b.deletedAt ?? '',
          ),
        render: (deleteBy?: string) =>
          deleteBy ?? format(new Date().toISOString(), DATE_FORMAT),
      },
      {
        title: (
          <Tooltip
            title={t('customer.statusTooltip', {
              statuses: DELETION_STATUS_KEYS.map((key) => t(key)).join(', '),
            })}>
            {t('customer.status')}
          </Tooltip>
        ),
        showSorterTooltip: false,
        dataIndex: 'status',
        key: 'status',
        sorter: (a: IDeletedCustomer, b: IDeletedCustomer) =>
          sortNumber(a.status ?? 0, b.status ?? 0),
        render: (status?: number) => {
          const key = DELETION_STATUS_KEYS[status ?? 0];
          return key ? t(key) : '';
        },
      },
      {
        title: t('common.actions'),
        key: 'actions',
        render: ({ id }: IDeletedCustomer) => (
          <DeletionQueueRowActions customerId={id ?? 0} />
        ),
      },
    ],
    [t, searchText, searchedColumn],
  );

  return (
    <Stack spacing={SIZE.M}>
      <Text variant="titleStandard" color="PrimaryMain">
        {t('customer.customersInQueueForDeletion')}
      </Text>
      <Table
        data-test-id="queue-table"
        data={data}
        isLoading={isLoading}
        columns={columns}
        getRowKey={getRowKey}
      />
    </Stack>
  );
};

export default withCustomerLayout(Queue);
